import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faSave, 
  faTrash, 
  faFileContract,
  faUser,
  faHome,
  faCalendarAlt,
  faMoneyBillWave,
  faCalculator,
  faSignOutAlt
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { contractService } from '../../services/contractService';
import { tenantService } from '../../services/tenantService';
import { propertyService } from '../../services/propertyService';
import { formatCurrency } from '../../utils/currencyFormatter';

const ContractEdit = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { userCompany, logout } = useAuth();
  
  const [contract, setContract] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [propertyUnits, setPropertyUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    tenantId: '',
    propertyUnitId: '',
    contractStartDate: '',
    contractEndDate: '',
    contractDuration: '',
    totalRentalAmount: '',
    chequeAmount: '',
    numberOfCheques: '',
    brokerageFee: '',
    brokeragePercentage: '',
    totalAmount: '',
    currency: 'USD',
    status: 'active',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [contractId, userCompany]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [contractData, tenantsData, unitsData] = await Promise.all([
        contractService.getContractById(contractId),
        tenantService.getTenantsByCompany(userCompany),
        propertyService.getUnitsByCompany(userCompany)
      ]);
      
      setContract(contractData);
      setTenants(tenantsData);
      setPropertyUnits(unitsData);
      
      // Populate form with existing data
      setFormData({
        tenantId: contractData.tenantId || '',
        propertyUnitId: contractData.propertyUnitId || '',
        contractStartDate: contractData.contractStartDate ? new Date(contractData.contractStartDate).toISOString().split('T')[0] : '',
        contractEndDate: contractData.contractEndDate ? new Date(contractData.contractEndDate).toISOString().split('T')[0] : '',
        contractDuration: contractData.contractDuration || '',
        totalRentalAmount: contractData.totalRentalAmount || '',
        chequeAmount: contractData.chequeAmount || '',
        numberOfCheques: contractData.numberOfCheques || '',
        brokerageFee: contractData.brokerageFee || '',
        brokeragePercentage: contractData.brokeragePercentage || '',
        totalAmount: contractData.totalAmount || '',
        currency: contractData.currency || 'USD',
        status: contractData.status || 'active',
        notes: contractData.notes || ''
      });
    } catch (error) {
      console.error('Error fetching contract data:', error);
      setError('Failed to load contract data: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateTotalAmount = () => {
    const totalRental = parseFloat(formData.totalRentalAmount) || 0;
    const brokerage = parseFloat(formData.brokerageFee) || 0;
    return totalRental + brokerage;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Validate required fields
      if (!formData.tenantId || !formData.propertyUnitId || !formData.contractStartDate || !formData.contractEndDate) {
        setError('Please fill in all required fields');
        return;
      }
      
      // Validate dates
      const startDate = new Date(formData.contractStartDate);
      const endDate = new Date(formData.contractEndDate);
      
      if (endDate <= startDate) {
        setError('End date must be after start date');
        return;
      }
      
      const updatedContract = {
        ...formData,
        contractStartDate: startDate.toISOString(),
        contractEndDate: endDate.toISOString(),
        totalAmount: calculateTotalAmount(),
        updatedAt: new Date()
      };
      
      await contractService.updateContract(contractId, updatedContract);
      
      setSuccess('Contract updated successfully!');
      setTimeout(() => {
        navigate(`/manager/contracts/${contractId}`);
      }, 1500);
      
    } catch (error) {
      console.error('Error updating contract:', error);
      setError('Failed to update contract: ' + (error.message || error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete contract "${contract?.id}"? This action cannot be undone.`)) {
      navigate('/manager/contracts', { state: { deleteContractId: contractId } });
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error && !contract) {
    return (
      <div className="container-fluid">
        <Alert variant="danger">
          <FontAwesomeIcon icon={faFileContract} className="me-2" />
          {error}
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/manager/contracts')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Contracts
        </Button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate(`/manager/contracts/${contractId}`)}
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Contract
          </Button>
          <div>
            <h2 className="mb-1">Edit Contract</h2>
            <p className="text-muted mb-0">
              <FontAwesomeIcon icon={faFileContract} className="me-1" />
              {contract?.id}
            </p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/dashboard')}>
            <FontAwesomeIcon icon={faHome} className="me-2" />
            Dashboard
          </Button>
          <Button variant="outline-danger" onClick={handleDelete}>
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Delete Contract
          </Button>

        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col lg={8}>
            {/* Contract Information */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faFileContract} className="text-primary me-2" />
                  Contract Information
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Contract ID</Form.Label>
                      <Form.Control
                        type="text"
                        value={contract?.id || ''}
                        disabled
                        className="bg-light"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status *</Form.Label>
                      <Form.Select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="active">Active</option>
                        <option value="expired">Expired</option>
                        <option value="terminated">Terminated</option>
                        <option value="pending">Pending</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Start Date *</Form.Label>
                      <Form.Control
                        type="date"
                        name="contractStartDate"
                        value={formData.contractStartDate}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>End Date *</Form.Label>
                      <Form.Control
                        type="date"
                        name="contractEndDate"
                        value={formData.contractEndDate}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Duration</Form.Label>
                      <Form.Control
                        type="text"
                        name="contractDuration"
                        value={formData.contractDuration}
                        onChange={handleInputChange}
                        placeholder="e.g., 12 months"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Currency</Form.Label>
                      <Form.Select
                        name="currency"
                        value={formData.currency}
                        onChange={handleInputChange}
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="AED">AED</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Additional notes about the contract..."
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            {/* Financial Details */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faMoneyBillWave} className="text-primary me-2" />
                  Financial Details
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Total Rental Amount *</Form.Label>
                      <Form.Control
                        type="number"
                        name="totalRentalAmount"
                        value={formData.totalRentalAmount}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Monthly Rent *</Form.Label>
                      <Form.Control
                        type="number"
                        name="chequeAmount"
                        value={formData.chequeAmount}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Number of Cheques</Form.Label>
                      <Form.Control
                        type="number"
                        name="numberOfCheques"
                        value={formData.numberOfCheques}
                        onChange={handleInputChange}
                        placeholder="12"
                        min="1"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Admin Fees</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="brokerageFee"
                        value={formData.brokerageFee}
                        onChange={handleInputChange}
                        placeholder="Enter brokerage fee amount"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Admin Fees Percentage (%)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="brokeragePercentage"
                        value={formData.brokeragePercentage}
                        onChange={handleInputChange}
                        placeholder="Enter brokerage percentage"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Total Amount</Form.Label>
                      <Form.Control
                        type="text"
                        value={formatCurrency(calculateTotalAmount(), formData.currency)}
                        readOnly
                        className="bg-light"
                      />
                      <Form.Text className="text-muted">
                        Total Rental Amount + Admin Fees
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            {/* Tenant Selection */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faUser} className="text-primary me-2" />
                  Tenant Selection
                </h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Select Tenant *</Form.Label>
                  <Form.Select
                    name="tenantId"
                    value={formData.tenantId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Choose a tenant...</option>
                    {tenants.map(tenant => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name} ({tenant.email})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                {formData.tenantId && (
                  <div className="alert alert-info">
                    <strong>Selected Tenant:</strong><br />
                    {tenants.find(t => t.id === formData.tenantId)?.name}
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Property Unit Selection */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faHome} className="text-primary me-2" />
                  Property Unit Selection
                </h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Select Property Unit *</Form.Label>
                  <Form.Select
                    name="propertyUnitId"
                    value={formData.propertyUnitId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Choose a property unit...</option>
                    {propertyUnits.map(unit => (
                      <option key={unit.id} value={unit.id}>
                        {unit.unitNumber} - {unit.unitType} ({unit.status})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                {formData.propertyUnitId && (
                  <div className="alert alert-info">
                    <strong>Selected Unit:</strong><br />
                    {propertyUnits.find(u => u.id === formData.propertyUnitId)?.unitNumber} - 
                    {propertyUnits.find(u => u.id === formData.propertyUnitId)?.unitType}
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Save Actions */}
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0">Actions</h5>
              </Card.Header>
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button 
                    type="submit" 
                    variant="primary"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} className="me-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline-secondary"
                    onClick={() => navigate(`/manager/contracts/${contractId}`)}
                  >
                    Cancel
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default ContractEdit; 